import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useMSSP } from '@/hooks/useIsMSSP';

export interface MonitoringClientOption {
  value: string;
  label: string;
}

interface MonitoringClients {
  /** The user's own client id (their organisation). */
  ownClientId: number | null;
  /** Display name of the user's own client. */
  ownClientName: string;
  /** True when the user has subclients and may switch identity. */
  isMSSP: boolean;
  /** All client ids the user may act on behalf of (own + subclients). */
  allowed: ReadonlySet<number>;
  /** Select options for the client picker (own first, subclients alphabetical). */
  options: ReadonlyArray<MonitoringClientOption>;
  /** Resolve a client id to its display name. */
  nameOf: (clientId: number | null) => string;
}

/**
 * The set of clients the current user can monitor "as": their own client plus
 * any subclients (when they are an MSSP). Provides picker options and a
 * name lookup for the active-client indicator.
 */
export const useMonitoringClients = (): MonitoringClients => {
  const auth = useAuth();
  const { subclientsObj, isMSSP } = useMSSP();

  return useMemo(() => {
    const ownClientId =
      auth.tokenInfo?.client_access_id != null ? Number(auth.tokenInfo.client_access_id) : null;
    const ownClientName = auth.tokenInfo?.client_name ?? 'My organization';

    const names = new Map<number, string>();
    if (ownClientId != null) names.set(ownClientId, ownClientName);

    const subclientOptions: MonitoringClientOption[] = Object.entries(subclientsObj ?? {})
      .map(([id, name]) => {
        const numericId = Number(id);
        names.set(numericId, name);
        return { value: String(numericId), label: name };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const options: MonitoringClientOption[] =
      ownClientId != null
        ? [{ value: String(ownClientId), label: `${ownClientName}` }, ...subclientOptions]
        : subclientOptions;

    return {
      ownClientId,
      ownClientName,
      isMSSP,
      allowed: new Set(names.keys()),
      options,
      nameOf: (clientId) => (clientId != null ? (names.get(clientId) ?? String(clientId)) : ''),
    };
  }, [auth.tokenInfo?.client_access_id, auth.tokenInfo?.client_name, subclientsObj, isMSSP]);
};

interface ActiveClient {
  /** The effective client id to act on behalf of (validated, falls back to own). */
  clientId: number | null;
  /** The user's own client id. */
  ownClientId: number | null;
  /** Whether the `:client` URL param is a client the user may act as. */
  isValid: boolean;
  /** False while token info is still loading (own client id may be unknown). */
  isAuthPending: boolean;
}

/**
 * Reads the active client id from the `:client` route param and validates it
 * against the clients the user may act on behalf of. When the param is missing
 * or unauthorised, `isValid` is false and `clientId` falls back to the user's
 * own client.
 */
export const useActiveClientId = (): ActiveClient => {
  const { client } = useParams();
  const { ownClientId, allowed } = useMonitoringClients();
  const { tokenInfoQuery } = useAuth();

  const parsed = client != null ? Number(client) : null;
  const isValid = parsed != null && Number.isFinite(parsed) && allowed.has(parsed);

  return {
    clientId: isValid ? parsed : ownClientId,
    ownClientId,
    isValid,
    isAuthPending: Boolean(tokenInfoQuery?.isPending),
  };
};
